import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';

export const HeroSlider = ({ slides }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!slides || slides.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [slides]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const handleButtonClick = (url) => {
    if (url.startsWith('http')) {
      window.open(url, '_blank');
    } else if (url.startsWith('/#')) {
      // Handle anchor links
      const element = document.getElementById(url.substring(2));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(url);
    }
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    }
    if (isRightSwipe) {
      goToPrevious();
    }
    
    // Reset touch positions
    setTouchStart(0);
    setTouchEnd(0);
  };

  if (!slides || slides.length === 0) return null;

  return (
    <div className="relative w-full max-w-6xl mx-auto mt-12 px-4 md:px-0">
      {/* Slider Container */}
      <div 
        className="relative min-h-[420px] md:h-96 rounded-2xl overflow-hidden backdrop-blur-sm border border-orange-500/20 shadow-2xl shadow-orange-500/10 hover:shadow-orange-500/30 hover:border-orange-500/40 transition-all duration-500 group"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Animated glow effect - Slower animation */}
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 via-green-500/10 to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-1000 animate-pulse-slow"></div>
        
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-all duration-1000 ${
              index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          >
            {/* Background Image for Mobile */}
            <div className="md:hidden absolute inset-0">
              <img
                src={slide.image}
                alt={slide.title}
                referrerPolicy="no-referrer"
                
                className="w-full h-full object-cover opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black/90"></div>
            </div>

            {/* Split Layout */}
            <div className="relative h-full flex items-center">
              {/* Left Side - Text Content */}
              <div className="w-full md:w-1/2 flex flex-col justify-center px-5 md:px-12 py-6 md:py-0 z-10">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400 animate-fade-in-up hover:scale-105 transition-transform duration-300 cursor-default leading-tight pb-2">
                  {slide.title}
                </h2>
                <p className="text-sm md:text-base lg:text-lg text-gray-100 mb-4 md:mb-6 animate-fade-in-up-delay hover:text-white transition-colors duration-300 line-clamp-5 md:line-clamp-none leading-relaxed">
                  {slide.description}
                </p>
                <div className="animate-fade-in-up-delay-2">
                  <Button
                    onClick={() => handleButtonClick(slide.buttonUrl)}
                    className="bg-gradient-to-r from-orange-500 to-green-500 hover:from-orange-600 hover:to-green-600 text-white px-6 md:px-8 py-2.5 md:py-3 text-sm md:text-base font-semibold shadow-lg shadow-orange-500/50 hover:shadow-orange-500/70 hover:scale-105 transition-all duration-300 rounded-full"
                  >
                    {slide.buttonText}
                  </Button>
                </div>
              </div>

              {/* Right Side - Image (Desktop Only) */}
              <div className="hidden md:block w-1/2 h-full relative group/image">
                <div className="absolute inset-0 overflow-hidden rounded-l-3xl">
                  <img
                    src={slide.image}
                    alt={slide.title}
                    referrerPolicy="no-referrer"
                    
                    className="w-full h-full object-contain group-hover/image:scale-105 transition-transform duration-700"
                  />
                  {/* Subtle gradient overlay on image */}
                  <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/20"></div>
                  {/* Enhanced hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-orange-500/20 via-transparent to-green-500/20 opacity-0 group-hover/image:opacity-100 transition-opacity duration-500"></div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation Arrows - Hidden on Mobile */}
        {slides.length > 1 && !isMobile && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-gradient-to-r hover:from-orange-500 hover:to-green-500 text-white p-3 rounded-full transition-all duration-300 hover:scale-125 hover:shadow-xl hover:shadow-orange-500/50 hover:rotate-[-15deg] backdrop-blur-sm border border-orange-500/20 hover:border-orange-500/60"
            >
              <ChevronLeft className="w-6 h-6 group-hover:animate-pulse" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-gradient-to-r hover:from-orange-500 hover:to-green-500 text-white p-3 rounded-full transition-all duration-300 hover:scale-125 hover:shadow-xl hover:shadow-green-500/50 hover:rotate-[15deg] backdrop-blur-sm border border-green-500/20 hover:border-green-500/60"
            >
              <ChevronRight className="w-6 h-6 group-hover:animate-pulse" />
            </button>
          </>
        )}
      </div>

      {/* Dots Navigation */}
      {slides.length > 1 && (
        <div className="flex justify-center mt-6 gap-3">
          {slides.map((slide, index) => (
            <button
              key={slide.id || `slide-dot-${index}`}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full hover:scale-125 ${
                index === currentSlide
                  ? 'w-12 h-3 bg-gradient-to-r from-orange-500 to-green-500 shadow-lg shadow-orange-500/50 animate-pulse'
                  : 'w-3 h-3 bg-gray-500 hover:bg-gradient-to-r hover:from-orange-400 hover:to-green-400 hover:w-6 hover:shadow-md hover:shadow-orange-400/50 hover:ring-2 hover:ring-orange-400/30'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};